/**
 * API client for Statements endpoints
 */

import apiClient from "./client";
import type {
  StatementListResponse,
  StatementResponse,
  ListStatementsParams,
} from "@/types/statement";

/**
 * Statements API endpoints
 */
export const statementsApi = {
  /**
   * List statements with pagination and filters
   */
  list: (params?: ListStatementsParams) =>
    apiClient.get<StatementListResponse>("/api/statements/v1/", { params }),

  /**
   * Every statement matching the filters, across all pages (for CSV export).
   * Pages through the list endpoint at its 100-row maximum.
   */
  listAll: async (
    params?: Omit<ListStatementsParams, "page" | "limit">,
    options?: {
      /** Abort the remaining pages (e.g. the user left the page). */
      signal?: AbortSignal;
      /** Called after each page with (fetched so far, total). */
      onProgress?: (fetched: number, total: number) => void;
    },
  ): Promise<StatementListResponse["items"]> => {
    const all: StatementListResponse["items"] = [];
    const limit = 100;
    for (let page = 1; ; page++) {
      const response = await apiClient.get<StatementListResponse>(
        "/api/statements/v1/",
        // The caller reports failures itself (and a cancel isn't one), so
        // the client must not toast "canceled" or double up.
        {
          params: { ...params, page, limit },
          signal: options?.signal,
          _skipToast: true,
        } as Record<string, unknown>,
      );
      all.push(...(response.data.items || []));
      options?.onProgress?.(all.length, response.data.meta?.total ?? all.length);
      if (page >= (response.data.meta?.total_pages ?? 1)) return all;
    }
  },

  /**
   * Get statement details by ID
   * @param statementId - Statement UUID
   */
  get: (statementId: string) =>
    apiClient.get<StatementResponse>(`/api/statements/v1/${statementId}`),

  /**
   * Download statement PDF
   * @param statementId - Statement UUID
   * @returns Blob of the PDF file
   */
  downloadPdf: (statementId: string) =>
    apiClient.get<Blob>(`/api/statements/v1/${statementId}/pdf`, {
      responseType: "blob",
    }),

  /**
   * Resend statement email to primary guardian
   * @param statementId - Statement UUID
   */
  resendEmail: (statementId: string) =>
    apiClient.post<{ message: string }>(`/api/statements/v1/${statementId}/resend-email`),

  /**
   * Generate statement for a family (MVP)
   * @param data - Family ID and month
   */
  generate: (data: { family_id: string; month: string }) =>
    apiClient.post<{ message: string }>("/api/statements/v1/generate", data),

  /**
   * Generate statement for a family from their financial activity
   * Creates a statement with opening balance, charges, payments, and closing balance
   */
  generateForFamily: (data: {
    family_id: string;
    period_start: string;
    period_end: string;
  }) =>
    apiClient.post<StatementResponse | { message: string }>(
      "/api/statements/v1/generate-for-family",
      data
    ),

  /**
   * Batch preflight check for statement generation
   * Returns preview of what will be generated
   */
  batchPreflight: (params: { period_start: string; period_end: string }) =>
    apiClient.get<{
      period_start: string;
      period_end: string;
      total_families: number;
      statements_to_generate: number;
      statements_already_generated: number;
      families: Array<{
        family_id: string;
        family_name: string;
        student_count: number;
        has_existing_statement: boolean;
      }>;
    }>("/api/statements/v1/batch-preflight", { params }),
};

export default statementsApi;
